import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { logError } from 'utils/datadog';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary for the public guest form route.
 *
 * That route sits outside the authenticated subtree, which is the only place with a react-router
 * `errorElement` — so without this, an uncaught render error (a stale dynamic-import chunk after a
 * deploy is the usual culprit) white-screens the respondent AND is never reported.
 *
 * Copy is hardcoded rather than translated on purpose: the boundary has to render even when what
 * failed is a provider inside the crashed subtree.
 */
export class GuestFormErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logError('Guest form render crash', error, {
      feature: 'guest-forms',
      componentStack: info.componentStack ?? undefined
    });
  }

  // Reloading re-redeems the token still in the URL and resumes at the saved answers, rather than
  // dead-ending on a blank screen.
  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 2,
          px: 3
        }}
      >
        <Typography variant="h4">Etwas ist schiefgelaufen</Typography>
        <Typography variant="body2" color="text.secondary">
          Bitte lade die Seite neu, um fortzufahren. Deine bisherigen Antworten
          sind gespeichert.
        </Typography>
        <Button variant="contained" onClick={this.handleReload}>
          Seite neu laden
        </Button>
      </Box>
    );
  }
}
